import styled, { css } from 'styled-components';
import { useCallback } from 'react';

import DrfqTakerOrderbookProvider, {
  useDrfqTakerOrderbook,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/drfq-taker-orderbook-context';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import useDrfqBbo from '@paradigm/features/src/unified-rfqs/contexts/use-drfq-bbo';
import { nepal, firefly } from '@paradigm/design-system/src/colors';
import { BboLeg } from '@paradigm/features/src/unified-rfqs/entities/drfq/bbo';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import { hourFormat, shortDateFormat } from '@paradigm/utils/src/date';

import {
  LegsTableContainer,
  Row,
  TableHeader,
} from '#/components/rfq-common/LegsTable';
import HeadedModal from '@paradigm/design-system/src/components/HeadedModal';
import {
  DateInfoMobile,
  LeftInfo,
  MobileRfqInfo,
} from '#/components/UnifiedRfqs/components/DetailsModal';
import Grid from '#/components/UnifiedRfqs/components/Grid';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { LegsContainer } from '#/components/UnifiedRfqs/components/MobileLegsTable';
import {
  useContextMenuActions,
  useContextMenuView,
} from '#/components/UnifiedRfqs/context-menu/context-menu';
import StrategyGreeks from '#/components/UnifiedRfqs/components/StrategyGreeks';
import MarkPrice from '#/components/UnifiedRfqs/components/MarkPrice';
import RfqDescription from '#/components/UnifiedRfqs/drfq/RfqDescription';
import DrfqDetailsLegsTable from '#/components/UnifiedRfqs/drfq/taker-rfq-details/DrfqDetailsLegsTable';
import CancelDrfqButton from '#/components/UnifiedRfqs/drfq/taker-rfq-details/CancelDrfqButton';
import { OrderbookDetailsModal } from '#/components/UnifiedRfqs/drfq/taker-rfq-details/OrderbookDetailsModal';
import {
  AnonMessage,
  WarningMessage,
} from '#/components/UnifiedRfqs/components/WarningMessage';
import {
  TrackingCtxProvider,
  useTrackAction,
} from '@paradigm/features/src/analytics';
import { QuoteBuilderProvider } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/form-context';
import DrfqMakersProvider from '@paradigm/features/src/unified-rfqs/ui/drfq/drfq-maker-orderbook-context';
import BboViewProvider, {
  useBboView,
} from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/view';
import {
  QuoteSubmitReport,
  useQuoteBuilderSubmit,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/submit-context';
import Button from '@paradigm/design-system/src/components/Button';
import {
  isValidDecimal,
  parseFormattedFloat,
  formatPrecision,
} from '@paradigm/utils/formatDecimal';
import { toast } from '#/components/UnifiedRfqs/toast';
import SubmittedProvider, {
  useSubmitted,
} from '@paradigm/features/src/forms/submitted-context';
import { useEstimatedPriceModalMethods } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/estimated-price-modal-context';
import HiddenLimitQuoteBuilderForm from '#/components/UnifiedRfqs/drfq/quote-builder/HiddenLimitQuoteBuilderForm';
import { HIDDEN_LIMIT_QUOTE_BUILDER_FORM_ID } from '#/components/UnifiedRfqs/drfq/constants';
import {
  primaryProductCode,
  strategyProductCodes,
} from '@paradigm/features/src/unified-rfqs/utils/legs';

interface Props {
  readonly close: () => void;
  readonly isOpen: boolean;
}

/**
 * DrfqHiddenLimitDetailsModal
 *
 * This component has been radically restructured to solve a specific UI layout issue:
 * - The Cancel RFQ and Submit Quote buttons need to be perfectly aligned at the bottom
 * - They must remain aligned regardless of scroll state, orderbook expansion, or content height
 *
 * Architectural Notes:
 * - Form submission logic has been moved from HiddenLimitQuoteBuilderForm to this component
 * - A shared footer with both buttons ensures visual alignment
 * - The keyboard binding for Enter key submission is preserved here
 * - The estimatedPriceModal is still in HiddenLimitQuoteBuilderForm but triggered from here
 *
 * This is an unusual architectural decision that splits responsibilities,
 * but it was necessary to achieve the precise layout required.
 */
export default function DrfqHiddenLimitDetailsModal() {
  const { isItemDetailsOpen } = useContextMenuView();
  const { closeDetailsModal } = useContextMenuActions();
  const drfq = useDrfqRfq();

  return (
    <TrackingCtxProvider value="rfq details">
      <DrfqTakerOrderbookProvider>
        <DrfqMakersProvider drfq={drfq}>
          <BboViewProvider>
            <QuoteBuilderProvider drfq={drfq}>
              <SubmittedProvider>
                <DrfqHiddenLimitDetailsModalContent
                  isOpen={isItemDetailsOpen}
                  close={closeDetailsModal}
                />
              </SubmittedProvider>
            </QuoteBuilderProvider>
          </BboViewProvider>
        </DrfqMakersProvider>
      </DrfqTakerOrderbookProvider>
    </TrackingCtxProvider>
  );
}

/**
 * Main modal content component that controls state and layout
 * for both mobile and desktop views
 */
function DrfqHiddenLimitDetailsModalContent({ isOpen, close }: Props) {
  const drfq = useDrfqRfq();
  const isMobile = useIsMobile();
  const { legs } = drfq;
  const view = useDrfqTakerOrderbook();
  const displayOrderbook =
    view.state === 'OPEN_AUCTION' &&
    (view.asks.length > 0 || view.bids.length > 0);
  const bbo = useBboView().data;
  const regularLegs = legs.filter((leg) => !leg.isHedge);
  const isSingleLeg = regularLegs.length === 1;
  const isAnonRFQ = drfq.is_taker_anonymous;
  const productCode = primaryProductCode(drfq.legs) ?? drfq.product_codes[0];
  // drfq.product_codes is [strategy_product_code, hedge_product_code] for a
  // hedged RFQ, so it can't be used to decide "is this multi-product".
  const productCodes = strategyProductCodes(drfq.legs);
  const modalWidth = isSingleLeg ? 975 : 1042;

  const { isValid, price } = useQuoteBuilderView();
  const { submitQuote, isLoading } = useQuoteBuilderSubmit();
  const estimatedPriceModal = useEstimatedPriceModalMethods();
  const { setSubmit } = useSubmitted();
  const trackAction = useTrackAction();

  /**
   * Quote submission handler - moved from HiddenLimitQuoteBuilderForm
   *
   * This handles validation, form submission, and API calls
   * for submitting hidden limit orders
   */
  const handleActualSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const [rawBid, rawAsk] = price.value;
      const bidPrice = isValidDecimal(rawBid)
        ? parseFormattedFloat(rawBid).toString()
        : undefined;
      const askPrice = isValidDecimal(rawAsk)
        ? parseFormattedFloat(rawAsk).toString()
        : undefined;

      const shouldSubmit =
        view.state === 'OPEN_AUCTION' &&
        (bidPrice !== undefined || askPrice !== undefined);

      trackAction('submit hidden limit order');
      setSubmit(true);
      if (!isValid) return;

      if (bidPrice == null && askPrice == null) {
        toast('You must submit a valid bid or ask price.');
        return;
      }

      if (!shouldSubmit) {
        close();
        return;
      }

      const onSubmitQuoteCallback = ({
        errors,
        estimatedPricesResp,
      }: QuoteSubmitReport) => {
        errors?.forEach((error) => toast(error));

        if (estimatedPricesResp != null) {
          estimatedPriceModal.open({ estimatedPricesResp });
          return;
        }
        if (errors?.length === 0) {
          close();
        }
      };
      submitQuote(onSubmitQuoteCallback);
    },
    [
      price.value,
      view,
      trackAction,
      setSubmit,
      isValid,
      close,
      submitQuote,
      estimatedPriceModal,
    ],
  );
  const rightPanelContent = (
    <RightPanelStyled>
      <HiddenLimitQuoteBuilderForm
        onSubmit={handleActualSubmit}
        greeks={
          <StrategyGreeks
            css="justify-content: start;"
            venue={drfq.venue}
            bbo={bbo}
          />
        }
      />
    </RightPanelStyled>
  );

  const desktopContent = (
    <ModalLayout>
      <MainContentArea>
        <LeftPanel>
          <Grid cols={1}>
            <RfqDescription drfq={drfq} isExecuted={false} />
            <DetailsLegsTableContainer isSingleLeg={isSingleLeg}>
              <DrfqDetailsLegsTable drfq={drfq} />
              {!isSingleLeg && <MarkPriceLegs drfq={drfq} />}
            </DetailsLegsTableContainer>
            {isAnonRFQ && <AnonWarningMessage />}
          </Grid>
          {displayOrderbook && <OrderbookDetailsModal />}
        </LeftPanel>
        {rightPanelContent}
      </MainContentArea>
      <SharedFooter>
        <CancelButtonWrapper>
          <CenterWrapper>
            <CancelDrfqButton rfqId={drfq.id} onCanceled={close} />
          </CenterWrapper>
        </CancelButtonWrapper>
        <SubmitButtonWrapper>
          <Button
            type="submit"
            form={HIDDEN_LIMIT_QUOTE_BUILDER_FORM_ID}
            ariaLabel="submit hidden limit order"
            isLoading={isLoading}
            disabled={!isValid || isLoading}
            css={css`
              width: 132px;
              height: 32px;
              @media screen and (max-width: 900px) {
                width: 148px;
                padding: 0 1rem;
              }
            `}
          >
            Submit Quote
          </Button>
        </SubmitButtonWrapper>
      </SharedFooter>
    </ModalLayout>
  );

  const mobileContent = (
    <ModalLayout>
      <MainContentArea>
        <LeftPanel>
          <MobileRfqInfo
            venue={drfq.venue}
            productCode={productCode}
            productCodes={productCodes}
            description={drfq.description}
          />
          <Grid cols={1}>
            <div css="margin: auto">
              <MarkPrice />
            </div>
            <LegsContainer>
              <DrfqDetailsLegsTable drfq={drfq} />
            </LegsContainer>
            {isAnonRFQ && <AnonWarningMessage />}
          </Grid>
          {displayOrderbook && <OrderbookDetailsModal />}
        </LeftPanel>
        {rightPanelContent}
      </MainContentArea>
      <SharedFooter>
        <CancelButtonWrapper>
          <CancelDrfqButton rfqId={drfq.id} onCanceled={close} />
        </CancelButtonWrapper>
        <SubmitButtonWrapper>
          <Button
            type="submit"
            ariaLabel="submit hidden limit order"
            form={HIDDEN_LIMIT_QUOTE_BUILDER_FORM_ID}
            isLoading={isLoading}
            disabled={!isValid || isLoading}
            css={css`
              width: 132px;
              height: 32px;
              @media screen and (max-width: 900px) {
                width: 148px;
                padding: 0 1rem;
              }
            `}
          >
            Submit Quote
          </Button>
        </SubmitButtonWrapper>
      </SharedFooter>
    </ModalLayout>
  );

  return (
    <HeadedModal
      title="Details"
      isOpen={isOpen}
      close={close}
      theme={isMobile ? 'primary-light' : 'default'}
      leftInfo={
        isMobile ? (
          <DateInfoMobile>
            <p>{hourFormat(drfq.last_updated_at)}</p>
            <p>{shortDateFormat(drfq.last_updated_at)}</p>
          </DateInfoMobile>
        ) : (
          <LeftInfo
            venue={drfq.venue}
            productCode={productCode}
            productCodes={productCodes}
          />
        )
      }
      width={modalWidth}
      contentCss={css`
        height: calc(100% - 56px);
        padding: 0;
      `}
      aria-label="unified-rfq rfq hidden limit details modal"
    >
      {isMobile ? mobileContent : desktopContent}
    </HeadedModal>
  );
}

const AnonWarningMessage = () => {
  return (
    <AnonMessageContainer>
      <WarningMessage>
        <AnonMessage />
      </WarningMessage>
    </AnonMessageContainer>
  );
};

/**
 * The layout consists of:
 * - ModalLayout: Flex column container for the entire modal content
 * - MainContentArea: Flex row container for left and right panels
 * - LeftPanel: Scrollable content area for main content
 * - RightPanelStyled: Container for form content
 * - SharedFooter: Fixed footer at bottom containing both buttons
 * - CancelButtonWrapper/SubmitButtonWrapper: Position buttons precisely
 */
const ModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
`;

export const RightPanelStyled = styled.div`
  width: 232px;
  background-color: ${firefly};
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  grid-template-rows: 1.5rem 1fr;
  justify-content: flex-end;
  min-height: 100%;
  overflow: hidden;
  margin-bottom: -1px;
`;

const MainContentArea = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const LeftPanel = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const SharedFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr 232px; /* Main area + right panel width */
  align-items: center;
  height: 64px;
  min-height: 64px;
  padding: 0;

  /* Left section */
  & > div:first-child {
    padding: 0 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    background-color: transparent;
    padding-block: 1rem;
  }

  /* Right section */
  & > div:last-child {
    background-color: ${firefly};
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 16px;
    position: relative;
  }
`;

const CancelButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
`;

const SubmitButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DetailsLegsTableContainer = styled.div<{ isSingleLeg: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.isSingleLeg ? '100% 0%' : '85% 15%'};
  gap: 5px;
`;

const AnonMessageContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  margin: 0 0 16px 0;
`;

const CenterWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`;

function MarkPriceLegs({ drfq }: { drfq: Drfq }) {
  const bbo = useDrfqBbo(drfq.id);
  return (
    <LegsTableContainer css="padding-top: 42px;">
      <TableHeader>
        <MarkPriceRow>
          <span>Mark Price</span>
        </MarkPriceRow>
      </TableHeader>
      {bbo?.legs.map((leg) => (
        <MarkPriceRowValue key={`${drfq.id}-${leg.instrument_id}`} leg={leg} />
      ))}
    </LegsTableContainer>
  );
}

interface MarkPriceRowValueProps {
  readonly leg: BboLeg;
}

function MarkPriceRowValue({ leg }: MarkPriceRowValueProps) {
  const { mark_price, mark_price_iv } = leg;

  return (
    <MarkPriceRowIv>
      {mark_price === '' ? NEGATIVE_SIGN : formatPrecision(mark_price, 4)}
      {mark_price_iv !== null && <MarkPriceIv>{mark_price_iv}%</MarkPriceIv>}
    </MarkPriceRowIv>
  );
}

export const MarkPriceRow = styled(Row)`
  grid-template-columns: auto;
`;

export const MarkPriceIv = styled.span`
  opacity: 0.3;
  font-size: 10px;
  color: #ffffff;
`;

export const MarkPriceRowIv = styled.div`
  color: ${nepal};
  display: flex;
  padding: 5px 0 0 12px;
  height: 42px;
  align-items: start;
  flex-direction: column;
`;
